/**
 * 6.D date input honouring the business event settings: the text shows `date_picker_format`
 * (MM/DD/YYYY default), the popover grid starts on `calendar_first_day`, the stored value is ISO `YYYY-MM-DD`.
 * (Plain `input[type=date]` is not used: its display format follows the browser locale, not the business setting.)
 */

import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import Icon from "../../ui/Icon";

const WEEKDAY_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]; // index = moment day()

const DateField = ({ value, onChange, format = "MM/DD/YYYY", firstDay = 0, min, disabled, ariaLabel, hasError }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [month, setMonth] = useState(() => (value ? moment(value, "YYYY-MM-DD") : moment()).startOf("month"));
  const ref = useRef(null);

  useEffect(() => {
    setText(value ? moment(value, "YYYY-MM-DD").format(format) : "");
    if (value) setMonth(moment(value, "YYYY-MM-DD").startOf("month"));
  }, [value, format]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commitText = () => {
    if (text.trim() === "") {
      onChange("");
      return;
    }
    const m = moment(text, format, true);
    if (m.isValid()) onChange(m.format("YYYY-MM-DD"));
    else setText(value ? moment(value, "YYYY-MM-DD").format(format) : "");
  };

  const pick = (m) => {
    onChange(m.format("YYYY-MM-DD"));
    setOpen(false);
  };

  // grid: from the first `firstDay` on/before the 1st, 6 weeks
  const start = month.clone();
  while (start.day() !== firstDay) start.subtract(1, "day");
  const cells = [];
  for (let i = 0; i < 42; i += 1) cells.push(start.clone().add(i, "day"));
  const headers = [];
  for (let i = 0; i < 7; i += 1) headers.push(WEEKDAY_SHORT[(firstDay + i) % 7]);
  const minM = min ? moment(min, "YYYY-MM-DD") : null;
  const selected = value ? moment(value, "YYYY-MM-DD") : null;

  return (
    <div className="eb-date-field eb-row-anchor" ref={ref}>
      <input
        type="text"
        value={text}
        placeholder={format}
        disabled={disabled}
        aria-label={ariaLabel}
        className={hasError ? "eb-has-error" : ""}
        onChange={(e) => setText(e.target.value)}
        onBlur={commitText}
        onFocus={() => !disabled && setOpen(true)}
        onKeyDown={(e) => e.key === "Enter" && commitText()}
        autoComplete="off"
      />
      {open ? (
        <div className="eb-date-pop" role="dialog" aria-label="Choose a date">
          <header>
            <button type="button" aria-label="Previous month" onClick={() => setMonth(month.clone().subtract(1, "month"))}>
              <Icon name="chevron" size={12} className="eb-rotate-180" />
            </button>
            <span>{month.format("MMMM YYYY")}</span>
            <button type="button" aria-label="Next month" onClick={() => setMonth(month.clone().add(1, "month"))}>
              <Icon name="chevron" size={12} />
            </button>
          </header>
          <div className="eb-date-grid">
            {headers.map((h, i) => (
              <span key={`h${i}`}>{h}</span>
            ))}
            {cells.map((d) => {
              const outside = d.month() !== month.month();
              const before = minM && d.isBefore(minM, "day");
              const isSel = selected && d.isSame(selected, "day");
              return (
                <button
                  type="button"
                  key={d.format("YYYY-MM-DD")}
                  className={`${outside ? "muted" : ""} ${isSel ? "selected" : ""}`.trim()}
                  disabled={before}
                  onClick={() => pick(d)}
                  aria-label={d.format("LL")}
                >
                  {d.date()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DateField;
